import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { descriptionForVariant } from '../../checksConfigPage/ChecksConfigPage';
import { VariantCheckbox } from '../../checksConfigPage/variantCheckbox/VariantCheckbox';
import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';
import { UseGraphSettingsButton } from '../common/UseGraphSettingsButton';
import { VariantCheckConfigFragment } from '../VariantCheckConfigFragment';

const variantCheckConfigIncludedVariantsMutation = gql`
  mutation VariantCheckConfigIncludedVariantsMutation(
    $graphId: ID!
    $graphVariant: String!
    $useGraphSettings: Boolean!
    $includedVariants: [String!]
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationIncludedVariants(
          useGraphSettings: $useGraphSettings
          includedVariants: $includedVariants
        ) {
          id
          ...VariantCheckConfigFragment
        }
      }
    }
  }
  ${VariantCheckConfigFragment}
`;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  useGraphSettings: Yup.boolean().required(),
  includedVariants: Yup.array(Yup.string()).nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

type Props = SharedCheckConfigCardProps & {
  allVariants: string[];
  statsLoading: boolean;
  statsData: Map<
    string,
    GraphQLTypes.ChecksConfigPageStatsQuery_service_operationCheckRequestsByVariant
  >;
};

export const IncludedVariantsCard = ({
  graphRef,
  graphCheckConfig,
  variantCheckConfig,
  allVariants,
  statsLoading,
  statsData,
  roles,
}: Props) => {
  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const useGraphSettings =
    variantCheckConfig.includedVariantsConfig.useGraphSettings;

  const [updateCheckConfig] = useMutation<
    GraphQLTypes.VariantCheckConfigIncludedVariantsMutation,
    GraphQLTypes.VariantCheckConfigIncludedVariantsMutationVariables
  >(variantCheckConfigIncludedVariantsMutation, {
    onError: setUpdateConfigError,
  });

  const { values, handleChange, setFieldValue, submitForm } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        useGraphSettings,
        includedVariants: useGraphSettings
          ? graphCheckConfig.includedVariants
          : variantCheckConfig.includedVariantsConfig.includedVariants,
      },
      onSubmit: async (formValues) => {
        await updateCheckConfig({
          variables: {
            graphId: graphRef.graphId,
            graphVariant: graphRef.graphVariant,
            useGraphSettings: formValues.useGraphSettings,
            includedVariants: formValues.includedVariants,
          },
        });
      },
    });

  const clearErrorsAndSubmit = useCallback(
    (e?: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setUpdateConfigError(null);
      if (e) {
        handleChange(e);
      }
      submitForm();
    },
    [setUpdateConfigError, handleChange, submitForm],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Included variants</CardTitle>
        <CardDescription>
          Operations reported for these variants will be checked against changes
          to the schema.
        </CardDescription>
        <FormControl>
          <UseGraphSettingsButton
            label="Use Graph Settings"
            tooltipContent={
              values.useGraphSettings
                ? 'Default to checking against reported operations for variants configured by this graph.'
                : undefined
            }
            isSelected={values.useGraphSettings}
            disabled={!roles.canWriteCheckConfiguration}
            onChange={(value) => {
              setFieldValue('useGraphSettings', value);
              if (value === true) {
                setFieldValue(
                  'includedVariants',
                  graphCheckConfig.includedVariants,
                );
              }
              clearErrorsAndSubmit();
            }}
          />
        </FormControl>
      </CardHeader>
      <CardBody>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gridAutoRows: '1fr',
            gridColumnGap: '1rem',
            gridRowGap: '1rem',
            alignContent: 'start',
          }}
          className="text-base"
        >
          {allVariants.map((variantName) => (
            <VariantCheckbox
              key={variantName}
              name="includedVariants"
              isChecked={values.includedVariants?.includes(variantName)}
              isDisabled={values.useGraphSettings}
              value={variantName}
              title={`${graphRef.graphId}@${variantName}`}
              description={descriptionForVariant(
                variantName,
                statsLoading,
                statsData,
              )}
              canWriteCheckConfiguration={roles.canWriteCheckConfiguration}
              onChange={clearErrorsAndSubmit}
            />
          ))}
        </div>

        {/* Form update error message */}
        {updateConfigError && <ErrorMessage error={updateConfigError} />}
      </CardBody>
    </Card>
  );
};
